import React, { useEffect } from 'react';

import { useLocation, useHistory } from 'react-router-dom';

import { Nav } from './Nav';

interface StepProps {
    buttonText: React.ReactNode;
    incrementStep: (e?: React.MouseEvent) => void;
}

interface StepDetail {
    BackLink?: React.ComponentType<{ children: React.ReactNode, className: string }>;
    back?: string;
    backText?: string;
    buttonText: React.ReactNode;
    component: React.ComponentType<StepProps>;
    next?: string;
}

interface StepperProps {
    content: React.ReactNode;
    stepDetails: Record<string, StepDetail>;
    steps: string[];
}

interface LocationState {
    step?: string;
}

const Stepper = ({
    content,
    stepDetails,
    steps,
    ...otherProps
}: StepperProps) => {
    const location = useLocation<LocationState>();
    const history = useHistory();
    const step = location?.state?.step ?? steps[0];

    useEffect(() => {
        const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        entries.forEach((entry) => {
            if (entry.type === 'reload') {
                goToStep(steps[0], { push: true });
            }
        });
    }, []);

    const {
        BackLink,
        back,
        backText,
        buttonText,
        component: Step,
        next,
    } = stepDetails[step] || {};

    const navigate = (stepToGoTo: string, opts?: { push: boolean }) => {
        const historyFn = opts?.push
            ? history.push
            : history.replace;

        historyFn({
            ...location,
            state: { step: stepToGoTo },
        });
    };

    const incrementStep = (e?: React.MouseEvent) => {
        e?.preventDefault();

        if (next) {
            navigate(next);
        }
    };

    const decrementStep = (e?: React.MouseEvent) => {
        e?.preventDefault();

        if (back) {
            navigate(back);
        }
    };

    const goToStep = (clickedStep: string, opts?: { push: boolean }) => {
        navigate(clickedStep, opts);
    };

    return (
        <>
            <Nav
                BackLink={BackLink}
                backText={backText}
                decrementStep={decrementStep}
                goToStep={goToStep}
                step={step}
                steps={steps}
            />
            {content}
            <Step
                buttonText={buttonText}
                incrementStep={incrementStep}
                {...otherProps}
            />
        </>
    );
};

export default Stepper;
